/**
 * Gateway authentication.
 *
 * The shapes come from `gateway-schema.d.ts`, generated from the gateway's own
 * Rust structs. They used to be written here by hand, which is how the browser
 * came to check `requires_totp` alone and never `requires_totp_setup`: anybody
 * whose organisation requires two-factor and who had not yet set it up fell
 * through to the signed-in branch, found no token, and was told "Login failed"
 * with nothing to do about it.
 */
import type { components } from './gateway-schema';
import { GATEWAY_URL, DEFAULT_API_URL, ApiError } from './client';

export type GatewayUser = components['schemas']['AuthenticatedUser'];
export type LoginResponse = components['schemas']['LoginResult'];
export type TotpEnrolment = components['schemas']['TotpQr'];

async function gatewayPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  // A wrong password is a 401 with a useful body, so parse before checking
  // status and let the caller read `.error` / `.requires_totp`.
  const json = (await res.json().catch(() => ({}))) as T & {
    success?: boolean;
    error?: string;
  };
  if (!res.ok && json.success === undefined) {
    throw new ApiError(json.error ?? `Login failed (${res.status})`, res.status);
  }
  return json;
}

export function login(email: string, password: string): Promise<LoginResponse> {
  return gatewayPost<LoginResponse>('/auth/login', { email: email.trim(), password });
}

export function verifyTotp(mfaToken: string, code: string): Promise<LoginResponse> {
  return gatewayPost<LoginResponse>('/auth/totp/verify', {
    mfa_token: mfaToken,
    code: code.trim(),
  });
}

/**
 * Start first-time two-factor enrolment: ask for the code to scan.
 *
 * Reached when login answers `requires_totp_setup`, meaning the organisation
 * requires two-factor and this account has none. Until the login page handled
 * that case the account simply could not get in.
 */
export function enrolTotp(mfaToken: string): Promise<TotpEnrolment> {
  return gatewayPost<TotpEnrolment>('/auth/totp/enroll', { mfa_token: mfaToken });
}

/** Finish enrolment with the first code from the app; signs them in. */
export function confirmTotp(mfaToken: string, code: string): Promise<LoginResponse> {
  return gatewayPost<LoginResponse>('/auth/totp/confirm', {
    mfa_token: mfaToken,
    code: code.trim(),
  });
}

/** Where this user's org server lives, falling back to the default host. */
export function resolveApiUrl(res: LoginResponse): string {
  return res.server_url || res.user?.tenant?.worker_url || DEFAULT_API_URL;
}

export type GoogleSignInConfig = components['schemas']['GoogleSignInConfig'];

/** Whether the gateway offers Google sign-in, and the client ID its button needs. */
export async function googleSignInConfig(): Promise<GoogleSignInConfig> {
  const res = await fetch(`${GATEWAY_URL}/auth/google/config`);
  // An older gateway without the endpoint simply has no Google sign-in.
  if (!res.ok) return { client_id: null };
  return (await res.json()) as GoogleSignInConfig;
}

/**
 * Sign in with the ID token Google's button handed back. The answer has the
 * same shape as a password login, two-factor challenge included.
 */
export function googleLogin(credential: string): Promise<LoginResponse> {
  return gatewayPost<LoginResponse>('/auth/google', { credential });
}
